// Entry point.
//
// Two modes off one renderer: the match (default) and the foil showcase
// (?showcase). Both share the GPU context, the card library and the frame
// graph; only the scene above them differs.

import { GpuContext, UnsupportedError } from './gfx/device.ts';
import { Renderer } from './gfx/renderer.ts';
import { CardLibrary } from './cards/library.ts';
import { Showcase, type ShowcaseHud } from './app/showcase.ts';
import { DOMAIN_COLORS, type CardData, type Domain } from './cards/types.ts';
import { loadRules } from './rules/load.ts';
import { Session } from './game/session.ts';
import { Board } from './game/board.ts';
import { Hud } from './game/hud.ts';
import type { Difficulty } from './ai/opponent.ts';
import { connectDiscord, displayName, inDiscord, markDiscordChrome } from './discord/activity.ts';
import type { DiscordUser } from './discord/activity.ts';
import {
  beginLogin, chooseGuest, completeLogin, guestChosen, isAuthCallback, savedAuth, signOut,
} from './discord/login.ts';
import { askToSignIn, hideSignIn } from './game/signin.ts';
import { holdUpdatesDuringPlay, registerServiceWorker } from './pwa.ts';
import { Setup } from './game/setup.ts';
import { Lobby } from './game/lobby.ts';
import { RemoteMatch } from './game/remote.ts';
import type { Match } from './game/match.ts';
import { Profiles, outcomeFor } from './game/profile.ts';
import { ReplayMatch } from './game/watch.ts';
import type { Replay } from './game/log.ts';
import type { ReplayMeta } from './game/profile.ts';
import type { SetupChoice } from './game/setup.ts';
import { WatchBar } from './game/replaybrowser.ts';
import { buildContext } from './rules/deck.ts';

const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

const boot = el('boot');
const bootBar = el('boot-bar');
const bootLabel = el('boot-label');

function progress(fraction: number, label: string): void {
  bootBar.style.width = `${Math.round(Math.min(1, Math.max(0, fraction)) * 100)}%`;
  bootLabel.textContent = label;
}

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

function fatal(title: string, detail: string): void {
  boot.classList.remove('done');
  boot.innerHTML = `<div class="fatal"><h1>${escapeHtml(title)}</h1><div>${detail}</div></div>`;
}

// -- showcase HUD ------------------------------------------------------------

const TYPE_LABEL: Record<string, string> = {
  unit: 'Unit', champion: 'Champion Unit', spell: 'Spell',
  signatureSpell: 'Signature Spell', gear: 'Gear', legend: 'Legend',
  battlefield: 'Battlefield', rune: 'Rune', token: 'Token',
};
const FOIL_LABEL = ['matte', 'foil trim', 'holographic', 'full-art holo'];

function buildShowcaseHud(): ShowcaseHud {
  const nameEl = el('card-name');
  const lineEl = el('card-line');
  const textEl = el('card-text');
  const statsEl = el('card-stats');
  const fpsEl = el('stats');

  return {
    setCard(card: CardData, index: number, total: number) {
      nameEl.textContent = card.name;

      const domains = card.domains.map((d: Domain) => {
        const [r, g, b] = DOMAIN_COLORS[d] ?? [0.6, 0.6, 0.6];
        const css = `rgb(${(r * 255) | 0} ${(g * 255) | 0} ${(b * 255) | 0})`;
        return `<span class="chip" style="color:${css}"><i class="dot" style="background:${css}"></i>${escapeHtml(d)}</span>`;
      }).join('');

      const tier = card.rarity === 'common' || card.rarity === 'uncommon' ? 1
        : card.rarity === 'rare' ? 2 : 3;

      lineEl.innerHTML = `
        <span>${escapeHtml(TYPE_LABEL[card.type] ?? card.type)}</span>
        <span class="chip ${card.rarity}">${escapeHtml(card.rarity)}</span>
        ${domains}
        <span class="chip" style="color:var(--gold)">${FOIL_LABEL[tier]}</span>`;

      // The text already carries the rules' own symbol shorthand ([M], [R]).
      // Reminder text is parenthesised, and is dimmed rather than stripped:
      // it explains the keywords, which is exactly what a card browser is for.
      textEl.innerHTML = card.text
        ? escapeHtml(card.text).replace(
          /(\([^)]*\))/g, '<em style="color:var(--ink-faint)">$1</em>')
        : '<span style="color:var(--ink-faint)">no rules text</span>';

      const bits: string[] = [];
      if (card.energy !== null) bits.push(`energy <b>${card.energy}</b>`);
      if (card.power !== null && card.power > 0) bits.push(`power <b>${card.power}</b>`);
      if (card.might !== null) bits.push(`might <b>${card.might}</b>`);
      bits.push(`<span style="color:var(--ink-faint)">${escapeHtml(card.set)} ${escapeHtml(card.collector)}</span>`);
      bits.push(`<span style="color:var(--ink-faint)">${index + 1}/${total}</span>`);
      statsEl.innerHTML = bits.join('');
    },
    setStats(text: string) {
      fpsEl.textContent = text;
    },
  };
}

/** What an uninvited visitor sees. Deliberately says nothing about the game. */
function showHoldingPage(): void {
  const boot = el('boot');
  boot.classList.remove('done');
  boot.innerHTML = `
    <div class="holding">
      <h1>Down for maintenance</h1>
      <p>This site is being worked on. Please check back later.</p>
    </div>`;
  document.title = 'Maintenance';
}

// -- boot --------------------------------------------------------------------

/**
 * Who is playing, in a browser.
 *
 * Runs before anything expensive, and answers with a token or with nothing.
 * A saved sign-in is *checked* rather than trusted: a token can be revoked or
 * simply time out, and finding that out after the card library has loaded
 * would mean showing this screen to somebody who has already waited.
 */
async function webIdentity(
  loginError?: string,
): Promise<{ token: string | null; user: DiscordUser | null }> {
  const guest = { token: null, user: null };

  let error = loginError;
  let expired = false;

  const saved = savedAuth();
  if (saved) {
    // `/api/me` is the only thing that can say whether a token is still good,
    // because the server is the only thing that asks Discord.
    const profile = await new Profiles(saved.token).me();
    if (profile) {
      return {
        token: saved.token,
        user: {
          id: profile.user.id,
          username: profile.user.username,
          globalName: profile.user.username,
          avatarUrl: profile.user.avatar
            ? `https://cdn.discordapp.com/avatars/${profile.user.id}/${profile.user.avatar}.png?size=128`
            : null,
        },
      };
    }
    signOut();
    expired = true;
  }

  // A choice already made is not asked again. That is most of what makes an
  // installed app feel unlike a web page -- it opens where you left it.
  if (!error && !expired && guestChosen()) return guest;

  // Somebody following a link to a shared match is not here to sign in. They
  // were sent one particular game to watch, and a sign-in wall in front of it
  // is the thing that makes a share link useless -- so the question is not
  // asked at all, and they arrive as a guest who can watch and nothing else.
  if (!error && new URLSearchParams(location.search).has('replay')) return guest;

  for (;;) {
    const choice = await askToSignIn({ error, expired });
    if (choice === 'guest') {
      chooseGuest();
      return guest;
    }
    // Leaves the page. Everything after this happens on the way back in, and
    // the promise is never resolved because the document is being torn down.
    beginLogin();
    await new Promise(() => {});
  }
}

/**
 * How hard to push the renderer, which is not the same question on a phone as
 * on a desktop.
 *
 * The two complaints pull in opposite directions and both are right. On a
 * desktop the card edges are jagged, because a rounded corner is resolved
 * through alpha-to-coverage and four MSAA samples give five coverage levels --
 * so the fix is more pixels. On a phone the frame time is already the problem:
 * a 3x panel at 4x MSAA plus the bloom chain is several times the work a
 * laptop is being asked for, for a screen where the jaggies are a third the
 * size. So the phone gets fewer pixels and the desktop gets more.
 *
 * The test is the same one the table's shape uses -- a device with only a
 * finger -- rather than a width, because a desktop window dragged narrow is
 * still a desktop with a desktop GPU behind it.
 */
function renderBudget(): { maxPixels: number; maxDpr: number; renderScale: number } {
  const phone = typeof matchMedia === 'function'
    && matchMedia('(pointer: coarse) and (hover: none)').matches;

  // `?render=1` forces a scale, which is the only way to compare the two on one
  // machine -- the same trick `?view=compact` plays for the table's shape.
  const forced = typeof location === 'object'
    ? Number(new URLSearchParams(location.search).get('render')) : NaN;
  if (Number.isFinite(forced) && forced > 0.25 && forced <= 4) {
    return { maxPixels: 8_000_000, maxDpr: 4, renderScale: forced };
  }

  if (phone) {
    // 1.5 rather than the panel's 3: two-thirds fewer pixels than a 3x buffer,
    // on a screen held far enough away that the difference is hard to find.
    // The budget is the second guard, for a tablet with a large 2x panel.
    return { maxPixels: 2_100_000, maxDpr: 1.5, renderScale: 1 };
  }
  // 1.5x supersampling, and the budget is what keeps it honest: at 1920x1080
  // it buys the full 2880x1620, and on a 4K panel it is clamped back to
  // roughly native rather than asking for 6K.
  return { maxPixels: 5_200_000, maxDpr: 3, renderScale: 1.5 };
}

async function main(): Promise<void> {
  // The OAuth return has to be handled before the curtain below, because
  // Discord sends the browser back to /auth and cannot be asked to add
  // `?show=true` to it -- a redirect is registered character for character.
  // It rewrites the address bar, so the flags are read afterwards.
  let loginError: string | undefined;
  if (isAuthCallback()) {
    const result = await completeLogin();
    if (result.error) loginError = result.error;
  }

  const flags = new URLSearchParams(location.search);
  const canvas = el<HTMLCanvasElement>('gpu');

  // The site is not meant to be browsable yet. Launched as a Discord Activity
  // it opens normally; a plain visitor gets a holding page unless they know
  // the parameter.
  //
  // This is a curtain, not a lock -- anyone who reads the page source finds
  // the parameter, and the build is still served to whoever asks for it. It
  // exists to keep the game from being *stumbled upon*, which is what was
  // wanted. Checked before WebGPU and before 137 MB of art starts loading, so
  // a passer-by costs nothing.
  markDiscordChrome();

  if (!inDiscord() && !flags.has('show')) {
    showHoldingPage();
    return;
  }

  // Installable from here on: the page is one someone is allowed to open, and
  // the worker is what lets it be launched from a home screen. Fire and
  // forget -- an install that does not take is a game that still runs.
  void registerServiceWorker();

  // In a browser, who is playing is asked before anything is downloaded. In
  // the Activity it is never asked at all: Discord has already answered.
  const web = inDiscord() ? { token: null, user: null } : await webIdentity(loginError);
  hideSignIn();

  // Running inside Discord is answered before anything expensive starts, so
  // the handshake overlaps with loading the renderer and the card library
  // rather than adding to it. It resolves to null everywhere else, and a
  // failure inside it is caught there: the match must not depend on it.
  const discord = connectDiscord().catch((err: unknown) => {
    console.warn('Discord unavailable:', err);
    return null;
  });

  progress(0.05, 'starting WebGPU…');
  const gpu = await GpuContext.create(canvas, renderBudget());

  progress(0.15, 'compiling shaders…');
  const renderer = new Renderer(gpu);

  const library = await CardLibrary.load(gpu.device, {
    onProgress: (done, total, label) => progress(0.15 + (done / total) * 0.45, label),
  });
  library.onEvict = (key) => renderer.invalidateVisual(key);

  const activity = await discord;
  if (activity) {
    document.body.dataset.discord = 'activity';
    progress(0.6, `hello, ${displayName(activity.user)}`);
  }

  const exposed: Record<string, unknown> = { gpu, renderer, library, discord: activity };

  if (flags.has('showcase')) {
    progress(0.65, 'loading artwork…');
    el('showcase-ui').hidden = false;
    const showcase = new Showcase(gpu, renderer, library, buildShowcaseHud());
    await library.preload(library.sample(12, 3));
    if (!flags.has('paused')) showcase.start();
    exposed.showcase = showcase;
  } else {
    progress(0.65, 'reading the rules…');
    const rules = await loadRules();

    // exactOptionalPropertyTypes: an absent option and one set to undefined are
    // different things, so build the object rather than spreading nullables in.
    // The difficulty is filled in from the deck screen below, where it is
    // chosen and remembered. `?ai=` still wins, and the deck screen shows the
    // choice as locked when it does rather than offering a control that is
    // quietly ignored.
    const sessionOptions: ConstructorParameters<typeof Session>[1] = {
      difficulty: (flags.get('ai') as Difficulty | null) ?? 'normal',
    };
    const seed = flags.get('seed');
    if (seed) sessionOptions.seed = seed;

    // The deck is chosen before anything is dealt. Signed in, that means the
    // player's saved decks and their record; signed out, it is a deck built
    // from the seed and an offer to paste one in for this match only.
    // One identity, whichever door it came through: the Activity handshake or
    // the browser's redirect. Everything downstream takes a token and a user
    // and has no business knowing which produced them.
    const token = activity?.token ?? web.token;
    const user = activity?.user ?? web.user;

    const profiles = token ? new Profiles(token) : null;
    const profile = profiles ? await profiles.me() : null;

    const ctx = buildContext(rules.index, rules.available);
    // The splash goes before the deck screen, not after the match starts: the
    // player is being asked something, and a loading bar behind it reads as
    // still loading.
    progress(1, 'ready');
    el('boot').classList.add('done');

    // The room, when there is one. Discord's instance id is the lobby: the
    // people who launched this Activity together are the people you can play.
    let setup: Setup | null = null;
    const lobby = activity?.token && activity.instanceId
      ? new Lobby(activity.instanceId, activity.token, (view) => setup?.onLobby(view))
      : null;
    if (lobby) await lobby.connect();

    // Outside Discord there is no instance id to be the room, so one is named
    // by a code instead. Offered only to a signed-in player: a room is a place
    // two people meet, and the server establishes who is in it from the token,
    // never from the message.
    const roomsByCode = !activity && token
      ? {
          createRoom: async (): Promise<string | null> => {
            const res = await fetch('/api/room-code', {
              method: 'POST', headers: { Authorization: `Bearer ${token}` },
            }).catch(() => null);
            if (!res?.ok) return null;
            return (await res.json() as { code?: string }).code ?? null;
          },
          joinRoom: async (code: string): Promise<Lobby | null> => {
            const joined = new Lobby(code, token, (view) => setup?.onLobby(view));
            try {
              await joined.connect();
              return joined;
            } catch (err) {
              console.warn('could not join room:', err);
              return null;
            }
          },
        }
      : {};

    setup = new Setup({
      ...roomsByCode,
      index: rules.index,
      ctx,
      available: rules.available,
      scripts: rules.scripts,
      profiles,
      stats: profile?.stats ?? null,
      playerName: user ? displayName(user) : 'Guest',
      avatarUrl: user?.avatarUrl ?? null,
      lobby,
      userId: user?.id ?? null,
      signInError: activity?.error,
    });
    // A shared match, opened from its link.
    //
    // Fetched without a token on purpose: a link you have to sign in to open
    // is not a link you can send anybody, so the server serves a *shared*
    // recording to whoever asks. The deck screen is skipped entirely -- the
    // player followed a link to one particular match, and asking them to
    // choose a deck first would be answering a question they did not ask.
    const shared = flags.get('replay');
    let choice: SetupChoice | null = null;
    if (shared) {
      const res = await fetch(`/api/replays/${encodeURIComponent(shared)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      }).catch(() => null);
      if (res?.ok) {
        const got = await res.json() as { replay: Replay; meta: ReplayMeta };
        choice = { mode: 'watch', replay: got.replay, meta: got.meta };
      } else {
        console.warn('that replay link could not be opened:', res?.status);
      }
    }
    if (!choice) choice = await setup.run();

    // From here a new build waits for a button. Everything up to this point --
    // boot, sign-in, the deck screen -- can be reloaded for free; a match
    // cannot, and an update that reloads mid-turn throws away a local game and
    // drops a remote one.
    holdUpdatesDuringPlay();
    if (choice.mode === 'ai') {
      sessionOptions.difficulty = choice.difficulty;
      if (choice.aiLegend) sessionOptions.aiLegend = choice.aiLegend;
    }

    // Two kinds of game, one board. A local session owns an engine; a remote
    // match owns nothing and relays to the server, which owns the only engine
    // that matters.
    let session: Match;
    let remote: RemoteMatch | null = null;
    let watching: ReplayMatch | null = null;
    if (choice.mode === 'watch') {
      // A recording is a third kind of match, and the board cannot tell: it
      // offers no legal actions, so every gesture is simply never legal and
      // nothing on screen has to be disabled by hand.
      watching = new ReplayMatch(rules, choice.replay);
      session = watching;
    } else if (choice.mode === 'pvp' && lobby) {
      remote = new RemoteMatch(rules, choice.frame,
        (table, action) => lobby.act(table, action),
        (table, object, cell) => lobby.arrange(table, object, cell));
      // Later frames -- the opponent moving, a spectator joining -- arrive on
      // the same stream the lobby was already reading.
      lobby.onMatch = (frame) => {
        if (frame.table === remote!.table) remote!.update(frame);
      };
      session = remote;
    } else {
      if (choice.mode === 'ai' && choice.deck) sessionOptions.humanDeck = choice.deck;
      session = new Session(rules, sessionOptions);
    }

    // Warm the art, but only wait for the part that is about to be on screen.
    //
    // This used to await *eighty* card renders -- both whole decks -- which is
    // about 14 MB of fetch, decode, mipmap and upload, and it was the entire
    // reason pressing "Play the AI" sat there for ten seconds. Almost none of
    // it was needed: the opening board is two legends, two champions, two
    // battlefields and the cards in your own hand, and the LRU that holds them
    // is 96 entries deep, so most of those eighty were evicted before anything
    // ever drew them.
    //
    // So the wait is the opening board and nothing else, and the rest of both
    // decks is warmed *behind* the match -- started, deliberately not awaited.
    // Pop-in was a real complaint and this still answers it; it just stops
    // answering it before the game is allowed to begin.
    if (session instanceof Session) {
      const state = session.state;
      const onScreen = new Set<string>();
      for (const object of Object.values(state.objects)) {
        const visible = object.zone.kind === 'board'
          // Their hand is card backs; only yours needs its faces.
          || (object.zone.kind === 'hand' && object.owner === session.human);
        if (visible) onScreen.add(object.card);
      }

      const byId = (ids: Iterable<string>): CardData[] => [...new Set(ids)]
        .map((id) => library.byId.get(id))
        .filter((c): c is CardData => !!c);

      await library.preload(byId(onScreen), (n, total) =>
        progress(0.75 + (n / total) * 0.2, `dealing… ${n}/${total}`));

      // Everything else, without holding the match up for it. Failures are
      // swallowed: this is a nicety, and `visualFor` streams on demand anyway.
      const rest = [
        ...session.decks[0].main, ...session.decks[0].runes,
        session.decks[0].legend, session.decks[0].chosenChampion, session.decks[0].battlefield,
        ...session.decks[1].main, ...session.decks[1].runes,
        session.decks[1].legend, session.decks[1].chosenChampion, session.decks[1].battlefield,
      ].filter((id) => !onScreen.has(id));
      // Capped at what the cache can actually hold. Asking for more than that
      // is asking the LRU to evict the things it just fetched.
      void library.preload(byId(rest).slice(0, 60)).catch(() => {});
    }

    let hud: Hud | null = null;
    const board = new Board(gpu, renderer, library, session, {
      onHover: (card) => hud?.setHovered(card),
      onState: () => hud?.update(),
      onPrompt: (text) => hud?.setPrompt(text),
      onOverlay: (items) => hud?.setOverlay(items),
      onInspect: (view) => hud?.setInspect(view),
      onScroll: (deltaY) => hud?.scrollDetail(deltaY) ?? false,
    });
    hud = new Hud(session, board);
    // A Discord Activity knows who is playing; a browser tab does not.
    if (activity?.user) {
      hud.setSeat('me', {
        name: displayName(activity.user),
        avatarUrl: activity.user.avatarUrl,
      });
    }
    // In a match against a person, both plates carry a name -- and the
    // opponent's comes from the room rather than from the card they played.
    if (remote && lobby) {
      const nameSeats = () => {
        const them = remote.seats[remote.human === 0 ? 1 : 0];
        const who = lobby.view.members.find((m) => m.id === them);
        if (who) {
          hud?.setSeat('them', {
            name: who.name,
            avatarUrl: who.avatar
              ? `https://cdn.discordapp.com/avatars/${who.id}/${who.avatar}.png?size=128`
              : null,
          });
        }
      };
      nameSeats();
      const previousView = lobby.onViewChange;
      lobby.onViewChange = () => { previousView?.(); nameSeats(); };
    }
    // Sound follows the engine's events, so the opponent's plays are audible
    // too. Chained rather than assigned: the result recorder also listens.
    const previousEvents = session.onEvents;
    session.onEvents = (events) => { previousEvents?.(events); hud?.onEvents(events); };

    hud.update();
    board.start();

    // 470/471. One row per finished match, recorded once. `onEvents` is the
    // only place that sees the game end exactly once -- polling `isOver` from
    // the render loop would file the same result on every frame.
    // A recording has already been recorded. Filing its ending again would
    // count one match every time anybody watched it.
    if (profiles && choice.mode !== 'watch') {
      let filed = false;
      const previous = session.onEvents;
      session.onEvents = (events) => {
        previous?.(events);
        if (filed) return;
        const over = events.find((e) => e.t === 'gameOver');
        if (!over) return;
        filed = true;
        void profiles.record(outcomeFor(over.winner, session.human), {
          ...(choice.deckId ? { deckId: choice.deckId } : {}),
          turns: session.state.turn,
        });
        // And the match itself, so it can be watched again. A replay is a
        // seed and a list of actions -- a few KB -- so keeping every finished
        // game costs less than one card image, and a game worth reviewing is
        // not one anybody thinks to save *before* it turns out to be
        // interesting.
        //
        // Only a local game. A match against a person is recorded by the
        // server that ran it: this client never held the seed, which is the
        // whole of how hidden information works, so the replay it can offer
        // has no actions in it at all.
        if (choice.mode === 'ai') {
          void profiles.saveReplay(session.replay(), {
            opponent: 'ai', versus: choice.difficulty,
          });
        }
      };
    }

    // The transport for a recording, once the board is drawing it.
    if (watching) {
      const bar = new WatchBar(watching, choice.mode === 'watch' ? choice.meta : null,
        () => { bar.hide(); location.reload(); });
      Object.assign(exposed, { watch: watching, bar });
    }

    Object.assign(exposed, { session, board, hud, rules, profiles, choice });
  }

  progress(1, 'ready');
  // The frame callback is there to let one frame paint under the splash before
  // it fades. A hidden tab never paints and never fires it, so the splash used
  // to sit over a fully booted match until the tab was looked at; the timer is
  // the floor that lifts it either way.
  let lifted = false;
  const lift = (): void => {
    if (lifted) return;
    lifted = true;
    boot.classList.add('done');
    setTimeout(() => boot.remove(), 600);
  };
  requestAnimationFrame(lift);
  setTimeout(lift, 400);

  Object.assign(globalThis as Record<string, unknown>, exposed);
}

main().catch((err: unknown) => {
  console.error(err);
  if (err instanceof UnsupportedError) {
    fatal('WebGPU is not available', escapeHtml(err.message));
  } else {
    const message = err instanceof Error ? err.message : String(err);
    const missingAssets = /Missing \//.test(message);
    fatal(
      missingAssets ? 'Assets are not built yet' : 'Something went wrong',
      missingAssets
        ? `${escapeHtml(message)}<br><br>That downloads the card database, the artwork, the foil masks and the compiled card effects into <code>assets/</code>.`
        : escapeHtml(message),
    );
  }
});
